'use client';

import React from 'react';
import { toast } from 'sonner';
import { AlertTriangle, AlertCircle, MinusCircle, Clock, ShoppingCart, ExternalLink } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { InventoryItem } from './inventoryData';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


interface Props {
  items: InventoryItem[];
}

const EXPIRY_ITEMS = [
  { id: 'exp-001', itemCode: 'VAC-003', itemName: "Marek\'s Disease Vaccine", store: 'Cold Store', expiryDate: '30/09/2026', daysLeft: 26, qty: 8, unit: 'doses×1000' },
  { id: 'exp-002', itemCode: 'FD-001', itemName: 'Broiler Starter Feed', store: 'Feed Store A', expiryDate: '30/11/2026', daysLeft: 87, qty: 840, unit: 'kg' },
  { id: 'exp-003', itemCode: 'VAC-001', itemName: 'Newcastle Disease Vaccine (La Sota)', store: 'Cold Store', expiryDate: '31/12/2026', daysLeft: 118, qty: 12, unit: 'doses×1000' },
];

export default function StockAlertPanel({ items }: Props) {
  const outOfStock = items.filter((i) => i.stockStatus === 'out-of-stock');
  const critical = items.filter((i) => i.stockStatus === 'critical');
  const low = items.filter((i) => i.stockStatus === 'low');

  const SEVERITY_GROUPS = [
    { id: 'sg-out', label: 'Out of Stock', items: outOfStock, icon: MinusCircle, color: 'var(--danger)', bg: 'var(--danger-bg)', border: '#fecaca' },
    { id: 'sg-crit', label: 'Critical Stock', items: critical, icon: AlertCircle, color: 'var(--danger)', bg: '#fff8f8', border: '#fecaca' },
    { id: 'sg-low', label: 'Low Stock', items: low, icon: AlertTriangle, color: 'var(--warning)', bg: 'var(--warning-bg)', border: '#fde68a' },
  ];

  return (
    <div className="space-y-4">
      {/* Stock level alerts */}
      {SEVERITY_GROUPS.map((group) => {
        if (group.items.length === 0) return null;
        const Icon = group.icon;
        return (
          <div key={group.id} className="panel-card overflow-hidden">
            <div
              className="flex items-center gap-3 px-4 py-3 border-b"
              style={{ backgroundColor: group.bg, borderColor: group.border }}
            >
              <Icon size={15} style={{ color: group.color }} />
              <h3 className="text-sm font-semibold" style={{ color: group.color }}>{group.label}</h3>
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full text-white ml-auto"
                style={{ backgroundColor: group.color, fontSize: '0.65rem' }}
              >
                {group.items.length} item{group.items.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
              {group.items.map((item) => (
                <div key={item.id} className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-muted/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono font-semibold" style={{ color: 'var(--primary)' }}>{item.itemCode}</span>
                      <StatusBadge status={item.stockStatus} size="sm" />
                    </div>
                    <p className="text-sm font-medium text-foreground truncate">{item.itemName}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.store} ·{' '}
                      <span className="font-tabular font-semibold" style={{ color: group.color }}>
                        {item.currentQty.toLocaleString()} {item.unit}
                      </span>
                      {' '}remaining
                      {item.daysRemaining !== null && (
                        <span className="font-semibold" style={{ color: group.color }}> (~{item.daysRemaining}d)</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Reorder level: {item.reorderLevel.toLocaleString()} {item.unit} · Supplier: {item.supplier}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => toast.info(`Raise purchase request for ${item.itemName}`)}
                      className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded transition-colors"
                      style={{ backgroundColor: 'var(--primary)', color: '#fff' }}
                    >
                      <ShoppingCart size={12} />
                      Request
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Expiry alerts */}
      <div className="panel-card overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'var(--border)', backgroundColor: '#fffbeb' }}>
          <Clock size={15} style={{ color: 'var(--warning)' }} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--warning)' }}>Expiring Items</h3>
          <span className="text-xs text-muted-foreground ml-1">Next 120 days</span>
          <span
            className="text-xs font-bold px-2 py-0.5 rounded-full text-white ml-auto"
            style={{ backgroundColor: 'var(--warning)', fontSize: '0.65rem' }}
          >
            {EXPIRY_ITEMS.length} items
          </span>
        </div>
        <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
          {EXPIRY_ITEMS.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-muted/50 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-mono font-semibold" style={{ color: 'var(--primary)' }}>{item.itemCode}</span>
                  <span
                    className="text-xs font-semibold px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: item.daysLeft < 30 ? 'var(--danger-bg)' : 'var(--warning-bg)',
                      color: item.daysLeft < 30 ? 'var(--danger)' : 'var(--warning)',
                    }}
                  >
                    {item.daysLeft}d left
                  </span>
                </div>
                <p className="text-sm font-medium text-foreground">{item.itemName}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {item.store} · {item.qty.toLocaleString()} {item.unit} · Expires: <strong>{item.expiryDate}</strong>
                </p>
              </div>
              <button
                onClick={() => toast.info(`Mark ${item.itemName} for priority use`)}
                className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded border transition-colors flex-shrink-0"
                style={{ borderColor: 'var(--warning)', color: 'var(--warning)' }}
              >
                <ExternalLink size={11} />
                Priority Use
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Summary actions */}
      <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg border" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}>
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground">
            {items.length} items require attention
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Raise purchase requests to avoid stockouts. Feed shortages directly impact flock performance.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href="/procurement"
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded"
            style={{ backgroundColor: 'var(--primary)', color: '#fff' }}
          >
            <ShoppingCart size={14} />
            Go to Procurement
          </Link>
        </div>
      </div>
    </div>
  );
}